import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { bookingApi, PublicLinkInfo, AvailabilityDay, AvailabilitySlot } from '../services/api';
import {
    Calendar,
    Clock,
    User,
    Mail,
    MessageSquare,
    Star,
    X,
    Check,
    ChevronLeft,
    ChevronRight,
    Globe,
    Send
} from 'lucide-react';

interface SelectedSlot extends AvailabilitySlot {
    date: string;
    day_name: string;
}

// Common timezones
const TIMEZONES = [
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'UTC', label: 'UTC' },
    { value: 'Europe/London', label: 'London (GMT/BST)' },
    { value: 'Europe/Paris', label: 'Central European Time' },
    { value: 'Asia/Tokyo', label: 'Japan Time' },
    { value: 'Australia/Sydney', label: 'Sydney Time' },
];

export function PublicBookingPage() {
    const { slug } = useParams<{ slug: string }>();

    // State
    const [link, setLink] = useState<PublicLinkInfo | null>(null);
    const [availability, setAvailability] = useState<AvailabilityDay[]>([]);
    const [timezone, setTimezone] = useState('America/New_York');
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [selectedSlots, setSelectedSlots] = useState<SelectedSlot[]>([]);
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    // Calendar navigation
    const [visibleWeekStart, setVisibleWeekStart] = useState(0);

    // Detect user's timezone
    useEffect(() => {
        try {
            const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (TIMEZONES.some(tz => tz.value === detected)) {
                setTimezone(detected);
            }
        } catch {
            // Keep default
        }
    }, []);

    // Load booking link data
    useEffect(() => {
        if (!slug) return;
        loadLink();
    }, [slug, timezone]);

    const loadLink = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const data = await bookingApi.getLink(slug!, timezone);
            setLink(data.link);
            setAvailability(data.availability);
        } catch (err: any) {
            setError(err.message || 'Failed to load booking page');
        } finally {
            setIsLoading(false);
        }
    };

    const handleTimezoneChange = (newTz: string) => {
        setTimezone(newTz);
        setSelectedSlots([]); // Clear selections when timezone changes
    };

    const toggleSlot = (slot: AvailabilitySlot, day: AvailabilityDay) => {
        const slotWithDate: SelectedSlot = {
            ...slot,
            date: day.date,
            day_name: day.day_name
        };

        setSelectedSlots(prev => {
            const exists = prev.some(s => s.start_time_utc === slot.start_time_utc);
            
            if (exists) {
                return prev.filter(s => s.start_time_utc !== slot.start_time_utc);
            }
            
            // Check max proposals limit
            if (link && prev.length >= link.max_proposals) {
                alert(`You can only select up to ${link.max_proposals} time preferences`);
                return prev;
            }
            
            return [...prev, slotWithDate];
        });
    };

    const isSlotSelected = (slot: AvailabilitySlot) => {
        return selectedSlots.some(s => s.start_time_utc === slot.start_time_utc);
    };

    const getSlotOrder = (slot: AvailabilitySlot) => {
        const index = selectedSlots.findIndex(s => s.start_time_utc === slot.start_time_utc);
        return index >= 0 ? index + 1 : null;
    };

    const removeSlot = (index: number) => {
        setSelectedSlots(prev => prev.filter((_, i) => i !== index));
    };

    const moveSlot = (from: number, to: number) => {
        if (to < 0 || to >= selectedSlots.length) return;
        setSelectedSlots(prev => {
            const result = [...prev];
            const [removed] = result.splice(from, 1);
            result.splice(to, 0, removed);
            return result;
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!name.trim() || !email.trim()) {
            alert('Please enter your name and email');
            return;
        }

        if (selectedSlots.length === 0) {
            alert('Please select at least one time preference');
            return;
        }

        if (!slug) return;

        setIsSubmitting(true);
        try {
            await bookingApi.submit(slug, {
                requester_name: name.trim(),
                requester_email: email.trim(),
                message: message.trim() || undefined,
                slots: selectedSlots.map(s => ({
                    start_time_utc: s.start_time_utc,
                    end_time_utc: s.end_time_utc
                })),
                timezone
            });
            setSubmitted(true);
        } catch (err: any) {
            alert(err.message || 'Failed to submit request');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Get visible days (show 7 at a time)
    const visibleDays = availability.slice(visibleWeekStart, visibleWeekStart + 7);
    const canGoBack = visibleWeekStart > 0;
    const canGoForward = visibleWeekStart + 7 < availability.length;

    if (isLoading) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0A2F5E 0%, #1A3A52 50%)'
            }}>
                <div className="spinner" />
            </div>
        );
    }

    if (error || !link) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0A2F5E 0%, #1A3A52 50%)',
                padding: 'var(--space-5)'
            }}>
                <div className="card" style={{ maxWidth: '400px', textAlign: 'center' }}>
                    <Calendar size={48} style={{ color: 'var(--color-gray-300)', margin: '0 auto var(--space-4)' }} />
                    <h2 style={{ color: 'var(--color-error)', marginBottom: 'var(--space-4)' }}>
                        Booking Unavailable
                    </h2>
                    <p style={{ color: 'var(--color-gray-500)' }}>
                        {error || 'This booking link is no longer active.'}
                    </p>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #0A2F5E 0%, #1A3A52 50%)',
                padding: 'var(--space-5)'
            }}>
                <div className="card" style={{ maxWidth: '500px', textAlign: 'center' }}>
                    <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        background: '#dcfce7',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto var(--space-4)'
                    }}>
                        <Check size={32} style={{ color: '#16a34a' }} />
                    </div>
                    <h2 style={{ marginBottom: 'var(--space-2)' }}>Request Submitted!</h2>
                    <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-4)' }}>
                        Your meeting request has been sent to <strong>{link.user_name}</strong>. 
                        You'll receive an email confirmation once they review your proposed times.
                    </p>
                    <div style={{ 
                        background: 'var(--color-gray-100)', 
                        padding: 'var(--space-4)', 
                        borderRadius: 'var(--radius-md)',
                        textAlign: 'left'
                    }}>
                        <h4 style={{ marginBottom: 'var(--space-2)' }}>Your proposed times:</h4>
                        {selectedSlots.map((slot, index) => (
                            <div key={slot.start_time_utc} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 'var(--space-2)',
                                padding: 'var(--space-1) 0',
                                fontSize: 'var(--font-size-sm)'
                            }}>
                                {index === 0 && <Star size={14} style={{ color: 'var(--color-primary)' }} />}
                                <span style={{ color: 'var(--color-gray-500)', minWidth: '20px' }}>#{index + 1}</span>
                                <span>{slot.day_name}, {slot.date} at {slot.start_time_local}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div style={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #0A2F5E 0%, #1A3A52 50%)',
            paddingBottom: 'var(--space-8)'
        }}>
            {/* Header */}
            <div style={{
                background: 'rgba(255,255,255,0.1)',
                padding: 'var(--space-5)',
                marginBottom: 'var(--space-4)'
            }}>
                <div className="container" style={{ maxWidth: '900px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'white' }}>
                        <Calendar size={24} />
                        <span style={{ fontSize: 'var(--font-size-lg)', fontWeight: 600 }}>
                            AAM <span style={{ color: 'var(--color-primary)' }}>Let's Meet</span>
                        </span>
                    </div>
                </div>
            </div>

            <div className="container" style={{ maxWidth: '900px' }}>
                {/* Link Info Card */}
                <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                    <h1 style={{ marginBottom: 'var(--space-2)' }}>{link.name}</h1>
                    <p style={{ color: 'var(--color-gray-500)', marginBottom: 'var(--space-3)' }}>
                        Schedule a meeting with <strong>{link.user_name}</strong>
                    </p>
                    
                    {link.description && (
                        <p style={{ marginBottom: 'var(--space-4)' }}>{link.description}</p>
                    )}

                    <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--color-gray-500)' }}>
                            <Clock size={16} />
                            {link.duration_minutes} minutes
                        </span>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--color-gray-500)' }}>
                            <Star size={16} />
                            Select up to {link.max_proposals} time{link.max_proposals > 1 ? 's' : ''}
                        </span>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--space-4)' }}>
                    {/* Calendar */}
                    <div className="card">
                        {/* Timezone & Navigation */}
                        <div style={{ 
                            display: 'flex', 
                            justifyContent: 'space-between', 
                            alignItems: 'center',
                            marginBottom: 'var(--space-4)',
                            flexWrap: 'wrap',
                            gap: 'var(--space-3)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Globe size={16} style={{ color: 'var(--color-gray-400)' }} />
                                <select
                                    className="form-select"
                                    value={timezone}
                                    onChange={(e) => handleTimezoneChange(e.target.value)}
                                    style={{ width: 'auto', fontSize: 'var(--font-size-sm)' }}
                                >
                                    {TIMEZONES.map(tz => (
                                        <option key={tz.value} value={tz.value}>{tz.label}</option>
                                    ))}
                                </select>
                            </div>

                            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <button
                                    onClick={() => setVisibleWeekStart(Math.max(0, visibleWeekStart - 7))}
                                    disabled={!canGoBack}
                                    className="btn btn-secondary btn-sm"
                                >
                                    <ChevronLeft size={18} />
                                </button>
                                <span style={{ minWidth: '150px', textAlign: 'center', fontSize: 'var(--font-size-sm)' }}>
                                    {visibleDays[0]?.formatted_date?.split(',')[0]} - {visibleDays[visibleDays.length - 1]?.formatted_date?.split(',')[0]}
                                </span>
                                <button
                                    onClick={() => setVisibleWeekStart(visibleWeekStart + 7)}
                                    disabled={!canGoForward}
                                    className="btn btn-secondary btn-sm"
                                >
                                    <ChevronRight size={18} />
                                </button>
                            </div>
                        </div>

                        {/* Availability Grid */}
                        {availability.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 'var(--space-8)', color: 'var(--color-gray-500)' }}>
                                <Calendar size={48} style={{ margin: '0 auto var(--space-4)', opacity: 0.5 }} />
                                <p>No availability in the selected time range.</p>
                            </div>
                        ) : (
                            <div style={{ 
                                display: 'grid', 
                                gridTemplateColumns: `repeat(${visibleDays.length}, 1fr)`,
                                gap: 'var(--space-2)'
                            }}>
                                {visibleDays.map(day => (
                                    <div key={day.date}>
                                        <div style={{ 
                                            textAlign: 'center', 
                                            padding: 'var(--space-2)',
                                            background: 'var(--color-gray-100)',
                                            borderRadius: 'var(--radius-sm)',
                                            marginBottom: 'var(--space-2)'
                                        }}>
                                            <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
                                                {day.day_name.substring(0, 3)}
                                            </div>
                                            <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-gray-500)' }}>
                                                {day.date.split('-').slice(1).join('/')}
                                            </div>
                                        </div>

                                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                            {day.slots.map(slot => {
                                                const selected = isSlotSelected(slot);
                                                const order = getSlotOrder(slot);

                                                return (
                                                    <button
                                                        key={slot.start_time_utc}
                                                        onClick={() => toggleSlot(slot, day)}
                                                        style={{
                                                            padding: 'var(--space-2)',
                                                            border: selected ? '2px solid var(--color-primary)' : '1px solid var(--color-gray-200)',
                                                            borderRadius: 'var(--radius-sm)',
                                                            background: selected ? 'var(--color-primary)' : 'white',
                                                            color: selected ? 'white' : 'var(--color-gray-700)',
                                                            cursor: 'pointer',
                                                            fontSize: 'var(--font-size-xs)',
                                                            transition: 'all var(--transition-fast)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '4px'
                                                        }}
                                                    >
                                                        {order && (
                                                            <span style={{
                                                                background: 'white',
                                                                color: 'var(--color-primary)',
                                                                borderRadius: '50%',
                                                                width: '16px',
                                                                height: '16px',
                                                                fontSize: '10px',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontWeight: 600
                                                            }}>
                                                                {order}
                                                            </span>
                                                        )}
                                                        {slot.start_time_local}
                                                    </button>
                                                );
                                            })}

                                            {day.slots.length === 0 && (
                                                <div style={{ 
                                                    padding: 'var(--space-2)', 
                                                    textAlign: 'center',
                                                    fontSize: 'var(--font-size-xs)',
                                                    color: 'var(--color-gray-400)'
                                                }}>
                                                    No availability
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Selection & Form */}
                    <div>
                        {/* Selected Times */}
                        <div className="card" style={{ marginBottom: 'var(--space-4)' }}>
                            <h3 style={{ marginBottom: 'var(--space-3)', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                                <Star size={18} />
                                Your Preferences
                                <span style={{ 
                                    fontSize: 'var(--font-size-xs)', 
                                    color: 'var(--color-gray-500)',
                                    fontWeight: 'normal'
                                }}>
                                    ({selectedSlots.length}/{link.max_proposals})
                                </span>
                            </h3>

                            {selectedSlots.length === 0 ? (
                                <p style={{ color: 'var(--color-gray-500)', fontSize: 'var(--font-size-sm)' }}>
                                    Click on available times to select your preferences
                                </p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                                    {selectedSlots.map((slot, index) => (
                                        <div
                                            key={slot.start_time_utc}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--space-2)',
                                                padding: 'var(--space-2)',
                                                background: index === 0 ? 'var(--color-primary-light)' : 'var(--color-gray-50)',
                                                borderRadius: 'var(--radius-sm)',
                                                border: index === 0 ? '1px solid var(--color-primary)' : '1px solid var(--color-gray-200)'
                                            }}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <button
                                                    onClick={() => moveSlot(index, index - 1)}
                                                    disabled={index === 0}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: index === 0 ? 'not-allowed' : 'pointer',
                                                        padding: 0,
                                                        opacity: index === 0 ? 0.3 : 1
                                                    }}
                                                >
                                                    <ChevronLeft size={12} style={{ transform: 'rotate(90deg)' }} />
                                                </button>
                                                <button
                                                    onClick={() => moveSlot(index, index + 1)}
                                                    disabled={index === selectedSlots.length - 1}
                                                    style={{
                                                        background: 'none',
                                                        border: 'none',
                                                        cursor: index === selectedSlots.length - 1 ? 'not-allowed' : 'pointer',
                                                        padding: 0,
                                                        opacity: index === selectedSlots.length - 1 ? 0.3 : 1
                                                    }}
                                                >
                                                    <ChevronLeft size={12} style={{ transform: 'rotate(-90deg)' }} />
                                                </button>
                                            </div>
                                            
                                            <div style={{ flex: 1, fontSize: 'var(--font-size-sm)' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    {index === 0 && <Star size={12} style={{ color: 'var(--color-primary)' }} />}
                                                    <span style={{ fontWeight: 500 }}>#{index + 1}</span>
                                                </div>
                                                <div style={{ color: 'var(--color-gray-600)' }}>
                                                    {slot.day_name} {slot.date}
                                                </div>
                                                <div>{slot.start_time_local} - {slot.end_time_local}</div>
                                            </div>

                                            <button
                                                onClick={() => removeSlot(index)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    padding: '4px',
                                                    color: 'var(--color-gray-400)'
                                                }}
                                            >
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Contact Form */}
                        <div className="card">
                            <h3 style={{ marginBottom: 'var(--space-3)' }}>Your Information</h3>

                            <form onSubmit={handleSubmit}>
                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <User size={14} />
                                        Name *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        placeholder="Your name"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <Mail size={14} />
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        className="form-input"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        placeholder="your@email.com"
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <MessageSquare size={14} />
                                        Message (optional)
                                    </label>
                                    <textarea
                                        className="form-textarea"
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        placeholder="What would you like to discuss?"
                                        rows={3}
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting || selectedSlots.length === 0}
                                    className="btn btn-primary btn-lg"
                                    style={{ width: '100%' }}
                                >
                                    {isSubmitting ? (
                                        <>
                                            <div className="spinner" style={{ width: '18px', height: '18px' }} />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            <Send size={18} />
                                            Request Meeting
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
